"use client"

import { createContext, useContext, useRef } from "react"
import { createStore, useStore } from "zustand"

// Define the store type
interface AuthDialogStore {
  open: boolean
  openDialog: () => void
  closeDialog: () => void
  onOpenChange: (open: boolean) => void
}

// Create the store
export const createAuthDialogStore = () =>
  createStore<AuthDialogStore>((set) => ({
    open: false,
    openDialog: () => set({ open: true }),
    closeDialog: () => set({ open: false }),
    onOpenChange: (open) => set({ open }),
  }))

// Create a context for the store
export const AuthDialogStoreContext = createContext<ReturnType<
  typeof createAuthDialogStore
> | null>(null)

// Provider component
export function AuthDialogProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const storeRef = useRef<ReturnType<typeof createAuthDialogStore>>(undefined)

  if (!storeRef.current) {
    storeRef.current = createAuthDialogStore()
  }

  return (
    <AuthDialogStoreContext.Provider value={storeRef.current}>
      {children}
    </AuthDialogStoreContext.Provider>
  )
}

// Hook to use the auth dialog store
export function useAuthDialog<T>(selector: (state: AuthDialogStore) => T): T {
  const store = useContext(AuthDialogStoreContext)

  if (!store) {
    throw new Error("useAuthDialog must be used within AuthDialogProvider")
  }

  return useStore(store, selector)
}
